import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Upload, X, ImageIcon, BadgePercent, Trash2, Plus, Crop, ZoomIn, ZoomOut, Ticket, ExternalLink, Facebook, MapPin, Check } from "lucide-react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { PostalCodeInput } from "../components/PostalCodeInput";
import { AddressInput } from "../components/AddressInput";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Slider } from "../components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { api, CATEGORIES, errorMessage, imageSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export default function EventEditorPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const { organizer } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newType, setNewType] = useState("");
  const [discountCodes, setDiscountCodes] = useState([]);
  const [dcForm, setDcForm] = useState({ code: "", type: "percent", value: "10", max_uses: "0" });
  const [dcSubmitting, setDcSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Konsert",
    venue: "",
    address: "",
    postal_code: "",
    city: "",
    date: "",
    capacity: 100,
    image_url: "",
    ticket_types: [],
    price: 0,
    type_prices: {},
    published: false,
    ticketing: "rightin", // "rightin" = billetter via Right in, "external" = Facebook-arrangement/annen billettside
    external_url: "",
    external_price_text: "",
    pay_at_door: false, // publikum kan velge «Betal ved inngangen» (QR-billett straks, merket ikke betalt)
  });

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/my/events/${id}`)
      .then((res) => {
        const e = res.data;
        setForm({
          title: e.title || "",
          description: e.description || "",
          category: e.category || "Annet",
          venue: e.venue || "",
          address: e.address || "",
          postal_code: e.postal_code || "",
          city: e.city || "",
          date: (e.date || "").slice(0, 16),
          capacity: e.capacity || 1,
          image_url: e.image_url || "",
          ticket_types: e.ticket_types || [],
          price: e.price || 0,
          type_prices: e.type_prices || {},
          published: !!e.published,
          ticketing: e.ticketing === "external" ? "external" : "rightin",
          external_url: e.external_url || "",
          external_price_text: e.external_price_text || "",
          pay_at_door: !!e.pay_at_door,
        });
        if (e.lat && e.lon) {
          setMapPoint({ lat: e.lat, lon: e.lon, label: [e.address, [e.postal_code, e.poststed || e.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") });
        }
      })
      .catch(() => {
        toast.error(t("Fant ikke arrangementet"));
        navigate("/dashboard");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const isExternal = form.ticketing === "external";
  const formIsPaid = !isExternal && (form.ticket_types.length > 0
    ? form.ticket_types.some((tt) => (parseInt(form.type_prices?.[tt], 10) || 0) > 0)
    : (parseInt(form.price, 10) || 0) > 0);
  // Kartforhåndsvisning: eksakt punkt fra valgt adresse (Kartverket) eller lagret arrangement, ellers omtrentlig fra teksten
  const [mapPoint, setMapPoint] = useState(null); // { lat, lon, label }
  const [approxQuery, setApproxQuery] = useState("");
  useEffect(() => {
    if (mapPoint) return undefined;
    const q = [form.venue, form.address, form.postal_code, form.city].map((x) => (x || "").trim()).filter(Boolean);
    const timer = setTimeout(() => setApproxQuery(form.address.trim() || form.postal_code.trim() ? [...q, "Norge"].join(", ") : ""), 700);
    return () => clearTimeout(timer);
  }, [form.venue, form.address, form.postal_code, form.city, mapPoint]);
  const mapSrc = mapPoint
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${mapPoint.lat},${mapPoint.lon}`)}&z=15&hl=no&output=embed`
    : approxQuery ? `https://www.google.com/maps?q=${encodeURIComponent(approxQuery)}&z=13&hl=no&output=embed` : "";
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapPoint ? `${mapPoint.lat},${mapPoint.lon}` : approxQuery)}`;
  const externalIsFacebook = /(^|\.)(facebook\.com|fb\.com|fb\.me)$/i.test((() => { try { return new URL(/^https?:\/\//i.test(form.external_url) ? form.external_url : `https://${form.external_url}`).hostname; } catch { return ""; } })());

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/my/events/${id}/discount-codes`)
      .then((res) => setDiscountCodes(res.data))
      .catch(() => {});
  }, [id, isEdit]);

  const addDiscountCode = async () => {
    const code = dcForm.code.trim();
    if (code.length < 2) {
      toast.error(t("Rabattkoden må ha minst 2 tegn"));
      return;
    }
    const value = parseInt(dcForm.value, 10) || 0;
    if (value < 1 || (dcForm.type === "percent" && value > 100)) {
      toast.error(dcForm.type === "percent" ? t("Prosent må være 1-100") : t("Beløpet må være minst 1 kr"));
      return;
    }
    setDcSubmitting(true);
    try {
      const { data } = await api.post(`/my/events/${id}/discount-codes`, {
        code,
        type: dcForm.type,
        value,
        max_uses: Math.max(0, parseInt(dcForm.max_uses, 10) || 0),
      });
      setDiscountCodes((prev) => [data, ...prev]);
      setDcForm({ code: "", type: dcForm.type, value: dcForm.value, max_uses: "0" });
      toast.success(t("Rabattkoden {code} er opprettet", { code: data.code }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDcSubmitting(false);
    }
  };

  const deleteDiscountCode = async (dc) => {
    try {
      await api.delete(`/my/discount-codes/${dc.id}`);
      setDiscountCodes((prev) => prev.filter((c) => c.id !== dc.id));
      toast.success(t("Rabattkoden {code} er slettet", { code: dc.code }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const addType = () => {
    const typeName = newType.trim();
    if (!typeName) return;
    if (form.ticket_types.includes(typeName)) {
      toast.error(t("Billettypen finnes allerede"));
      return;
    }
    if (form.ticket_types.length >= 10) {
      toast.error(t("Maks 10 billettyper"));
      return;
    }
    setForm((f) => ({ ...f, ticket_types: [...f.ticket_types.filter((x) => x !== typeName), typeName], type_prices: { ...f.type_prices, [typeName]: f.type_prices[typeName] ?? 0 } }));
    setNewType("");
  };

  const removeType = (typeName) =>
    setForm((f) => {
      const tp = { ...f.type_prices };
      delete tp[typeName];
      return { ...f, ticket_types: f.ticket_types.filter((x) => x !== typeName), type_prices: tp };
    });

  const setTypePrice = (typeName, value) => {
    const v = Math.max(0, parseInt(value || "0", 10) || 0);
    setForm((f) => ({ ...f, type_prices: { ...f.type_prices, [typeName]: v } }));
  };

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const mb = (n) => (n / (1024 * 1024)).toFixed(1).replace(".", ",");

  // Nedskalerer/komprimerer et for stort bilde i nettleseren (canvas -> JPEG)
  const compressImage = (file, maxDim = 1920, quality = 0.82) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("komprimering feilet"))),
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("kunne ikke lese bildet"));
      };
      img.src = url;
    });

  const uploadImageFile = async (file) => {
    let payload = file;
    if (file.size > MAX_IMAGE_BYTES) {
      try {
        const blob = await compressImage(file);
        if (blob.size > MAX_IMAGE_BYTES) {
          toast.error(
            t("Bildet er for stort ({size} MB, maks 5 MB) og lot seg ikke komprimere nok. Forslag: reduser oppløsningen til ca. 1600 px bredde, eller lagre som JPG med lavere kvalitet før du laster opp.", { size: mb(file.size) })
          );
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
        payload = new File([blob], (file.name.replace(/\.[^.]+$/, "") || "bilde") + ".jpg", { type: "image/jpeg" });
        toast.success(t("Bildet var {from} MB – vi komprimerte det til {to} MB for deg", { from: mb(file.size), to: mb(blob.size) }));
      } catch {
        toast.error(
          t("Bildet er for stort ({size} MB, maks 5 MB). Forslag: reduser oppløsningen (f.eks. 1600 px bredde) eller lagre som JPG med lavere kvalitet før du laster opp.", { size: mb(file.size) })
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", payload);
      const { data } = await api.post("/my/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success(t("Bildet er lastet opp"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke laste opp bildet")));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Beskjæring: fil velges -> dialog med 16:9-utsnitt (dra/zoom) -> opplasting
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [cropArea, setCropArea] = useState(null);
  const pendingFileRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pendingFileRef.current = file;
    setCropPos({ x: 0, y: 0 });
    setCropZoom(1);
    setCropArea(null);
    setCropSrc(URL.createObjectURL(file));
    setCropOpen(true);
  };

  const openCropExisting = () => {
    pendingFileRef.current = null;
    setCropPos({ x: 0, y: 0 });
    setCropZoom(1);
    setCropArea(null);
    setCropSrc(imageSrc(form.image_url));
    setCropOpen(true);
  };

  const closeCrop = () => {
    setCropOpen(false);
    setCropSrc((old) => {
      if (old && old.startsWith("blob:")) URL.revokeObjectURL(old);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyCrop = async () => {
    if (!cropArea || !cropSrc) return;
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error("kunne ikke lese bildet"));
        i.src = cropSrc;
      });
      const outW = Math.min(1920, Math.max(320, Math.round(cropArea.width)));
      const outH = Math.round((outW * 9) / 16);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      canvas.getContext("2d").drawImage(img, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, outW, outH);
      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("beskjæring feilet"))), "image/jpeg", 0.85)
      );
      closeCrop();
      await uploadImageFile(new File([blob], "bilde.jpg", { type: "image/jpeg" }));
    } catch {
      toast.error(t("Kunne ikke beskjære bildet – prøv «Bruk uten beskjæring» eller et annet bilde"));
    }
  };

  const uploadOriginal = async () => {
    const file = pendingFileRef.current;
    closeCrop();
    if (file) await uploadImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) {
      toast.error(t("Fyll inn tittel og dato"));
      return;
    }
    if (isExternal && !form.external_url.trim()) {
      toast.error(t("Legg inn lenken til Facebook-arrangementet eller billettsiden"));
      return;
    }
    if (isExternal && !form.address.trim() && !form.postal_code.trim()) {
      toast.error(t("Eksterne arrangement må ha gateadresse eller postnummer, slik at de vises i riktig kommune"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        capacity: parseInt(form.capacity, 10) || 1,
        price: Math.max(0, parseInt(form.price, 10) || 0),
        type_prices: Object.fromEntries(
          Object.entries(form.type_prices || {}).map(([k, v]) => [k, Math.max(0, parseInt(v, 10) || 0)])
        ),
      };
      let saved;
      if (isEdit) {
        saved = (await api.put(`/my/events/${id}`, payload)).data;
        toast.success(t("Endringene er lagret"));
      } else {
        saved = (await api.post("/my/events", payload)).data;
        toast.success(form.published ? t("Arrangementet er opprettet og publisert!") : t("Arrangementet er lagret som kladd"));
      }
      if (saved?.published && saved?.vipps_pending) {
        toast.info(t(saved.message || "Publisert – publikum kan reservere og betale ved inngangen; Vipps-betaling åpner når Vipps er satt opp"), {
          duration: 9000,
          action: { label: t("Gå til Betaling"), onClick: () => navigate("/dashboard/betaling") },
        });
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6" data-testid="editor-back-link">
          <ArrowLeft className="w-4 h-4" />
          {t("Tilbake til oversikten")}
        </Link>
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-2xl font-heading" data-testid="editor-title">
              {isEdit ? t("Rediger arrangement") : t("Nytt arrangement")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="ev-title">{t("Tittel")} *</Label>
                <Input
                  id="ev-title"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder={t("F.eks. Sommerkonsert i parken")}
                  required
                  data-testid="editor-title-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Kategori")}</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="editor-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-date">{t("Dato og klokkeslett")} *</Label>
                  <Input
                    id="ev-date"
                    type="datetime-local"
                    value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                    required
                    data-testid="editor-date-input"
                  />
                </div>
              </div>
              {/* Billetter via Right in eller eksternt arrangement (Facebook / annen billettside) */}
              <div className="space-y-2 rounded-xl border border-border p-4" data-testid="editor-ticketing-section">
                <Label>{t("Billetter")}</Label>
                <RadioGroup value={form.ticketing} onValueChange={(v) => set("ticketing", v)} className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="editor-ticketing-radio">
                  <label htmlFor="ticketing-rightin" className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${!isExternal ? "border-brand bg-brand-soft/40" : "border-border hover:bg-secondary/60"}`}>
                    <RadioGroupItem value="rightin" id="ticketing-rightin" className="mt-0.5" data-testid="editor-ticketing-rightin" />
                    <span className="text-sm">
                      <span className="font-medium flex items-center gap-1.5"><Ticket className="w-4 h-4 text-brand" /> {t("Billetter via Right in")}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{t("Gratis eller betalte billetter med QR-kode, kapasitet, venteliste og innsjekk.")}</span>
                    </span>
                  </label>
                  <label htmlFor="ticketing-external" className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${isExternal ? "border-brand bg-brand-soft/40" : "border-border hover:bg-secondary/60"}`}>
                    <RadioGroupItem value="external" id="ticketing-external" className="mt-0.5" data-testid="editor-ticketing-external" />
                    <span className="text-sm">
                      <span className="font-medium flex items-center gap-1.5"><ExternalLink className="w-4 h-4 text-brand" /> {t("Eksternt arrangement")}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{t("Vises på Right in som de andre, men billetter/påmelding skjer på Facebook eller en annen billettside.")}</span>
                    </span>
                  </label>
                </RadioGroup>
                {isExternal && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2" data-testid="editor-external-fields">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="ev-external-url">{t("Lenke til Facebook-arrangementet eller billettsiden")} *</Label>
                      <div className="relative">
                        {externalIsFacebook ? <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /> : <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />}
                        <Input
                          id="ev-external-url"
                          type="url"
                          inputMode="url"
                          value={form.external_url}
                          onChange={(e) => set("external_url", e.target.value)}
                          placeholder="https://www.facebook.com/events/…"
                          className="pl-9"
                          required={isExternal}
                          data-testid="editor-external-url-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("Publikum sendes hit med knappen «Gå til arrangementet». Right in selger ikke billetter til eksterne arrangement.")}</p>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="ev-external-price">{t("Pris (fritekst, valgfritt)")}</Label>
                      <Input
                        id="ev-external-price"
                        value={form.external_price_text}
                        onChange={(e) => set("external_price_text", e.target.value)}
                        placeholder={t("F.eks. Gratis, 250 kr, eller «Se pris på Facebook»")}
                        maxLength={80}
                        data-testid="editor-external-price-input"
                      />
                    </div>
                    <p className="sm:col-span-2 text-xs text-muted-foreground flex items-start gap-1.5" data-testid="editor-external-geo-note">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t("Eksterne arrangement må ha gateadresse eller postnummer i Norge, slik at de vises i riktig kommune.")}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ev-venue">{t("Sted / lokale")}</Label>
                  <Input
                    id="ev-venue"
                    value={form.venue}
                    onChange={(e) => set("venue", e.target.value)}
                    placeholder={t("F.eks. Sentrum Scene")}
                    data-testid="editor-venue-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-city">{t("By")}</Label>
                  <Input
                    id="ev-city"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    placeholder={t("F.eks. Oslo")}
                    data-testid="editor-city-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ev-address">{t("Gateadresse (vises på kart)")}{isExternal ? " *" : ""}</Label>
                  <AddressInput
                    id="ev-address"
                    value={form.address}
                    onChange={(v) => { set("address", v); setMapPoint(null); }}
                    onSelect={(a) => {
                      setForm((f) => ({ ...f, address: a.adressetekst, postal_code: a.postnummer || f.postal_code, city: a.poststed || f.city }));
                      if (a.lat && a.lon) setMapPoint({ lat: a.lat, lon: a.lon, label: `${a.adressetekst}, ${a.postnummer} ${a.poststed}` });
                    }}
                    postalCode={form.postal_code}
                    placeholder={t("F.eks. Arbeidergata 1")}
                    testId="editor-address-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-postal">{t("Postnummer")}{isExternal ? " *" : ""}</Label>
                  <PostalCodeInput
                    id="ev-postal"
                    value={form.postal_code}
                    onChange={(v) => set("postal_code", v)}
                    onPlace={(p) => set("city", p.poststed)}
                    testId="editor-postalcode-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-3">{t("Med adresse/postnummer vises et Google Maps-kart på arrangementssiden, og arrangementet kan finnes via posisjon, postnummer og kommune.")}</p>
              {mapSrc && (
                <div className="rounded-xl border border-border overflow-hidden bg-muted" data-testid="editor-map-preview" data-exact={mapPoint ? "true" : "false"}>
                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs bg-card border-b border-border">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {mapPoint ? <Check className="w-3.5 h-3.5 text-[hsl(var(--success))] shrink-0" /> : <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <span className="truncate" data-testid="editor-map-caption">
                        {mapPoint ? <>{t("Kartpunkt")}: <strong>{mapPoint.label}</strong></> : t("Omtrentlig plassering – velg en adresse fra forslagene for eksakt kartpunkt")}
                      </span>
                    </span>
                    <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand underline underline-offset-4 shrink-0" data-testid="editor-map-open-link">
                      {t("Åpne i Google Maps")} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <iframe
                    title={t("Kartforhåndsvisning")}
                    src={mapSrc}
                    className="w-full h-[220px] border-0 block"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="editor-map-iframe"
                  />
                </div>
              )}
              {!isExternal && (
                <>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-capacity">{t("Kapasitet (antall stoler/plasser)")} *</Label>
                  <Input
                    id="ev-capacity"
                    type="number"
                    min={1}
                    max={100000}
                    value={form.capacity}
                    onChange={(e) => set("capacity", e.target.value)}
                    required
                    data-testid="editor-capacity-input"
                  />
                  <p className="text-xs text-muted-foreground">{t("Fri plassering – plassene er ikke nummererte. Kapasiteten styrer kun hvor mange billetter som kan hentes.")}</p>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Billettyper (valgfritt)")}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addType();
                        }
                      }}
                      placeholder={t("F.eks. Voksen, Barn, Honnør...")}
                      data-testid="editor-tickettype-input"
                    />
                    <Button type="button" variant="secondary" onClick={addType} data-testid="editor-tickettype-add-button">
                      {t("Legg til")}
                    </Button>
                  </div>
                  {form.ticket_types.length > 0 ? (
                    <div className="space-y-2 pt-1" data-testid="editor-tickettype-list">
                      {form.ticket_types.map((tt) => (
                        <div key={tt} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2">
                          <span className="flex-1 text-sm font-medium truncate">{tt}</span>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              min={0}
                              value={form.type_prices[tt] ?? 0}
                              onChange={(e) => setTypePrice(tt, e.target.value)}
                              className="h-9 w-24 text-right"
                              data-testid={`editor-tickettype-price-${tt}`}
                            />
                            <span className="text-xs text-muted-foreground">kr</span>
                          </div>
                          <button type="button" onClick={() => removeType(tt)} className="rounded-full hover:bg-secondary p-1.5" data-testid={`editor-tickettype-remove-${tt}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 pt-1">
                      <Label htmlFor="ev-price" className="text-sm text-muted-foreground shrink-0">{t("Pris per billett:")}</Label>
                      <Input
                        id="ev-price"
                        type="number"
                        min={0}
                        value={form.price}
                        onChange={(e) => set("price", e.target.value)}
                        className="h-9 w-28 text-right"
                        data-testid="editor-price-input"
                      />
                      <span className="text-xs text-muted-foreground">{t("kr (0 = gratis)")}</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{t("Typene deler den samme totalkapasiteten. Pris over 0 kr betales med Vipps når Vipps er satt opp – inntil da kan publikum reservere og betale ved inngangen.")}</p>
                </div>
                {formIsPaid && (
                  <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4" data-testid="editor-pay-at-door-section">
                    <div className="space-y-1">
                      <Label htmlFor="ev-pay-at-door" className="font-medium">{t("Tillat «Betal ved inngangen»")}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t("Publikum kan velge å ikke betale med Vipps. De får QR-billett med en gang, tydelig merket «ikke betalt», og betaler til deg ved inngangen. Slike billetter teller som solgt, og du får en egen utskriftsliste under Deltakere.")}
                      </p>
                      <p className="text-xs text-muted-foreground" data-testid="editor-pay-at-door-auto-note">
                        {t("Er Vipps ikke satt opp ennå, er «Reserver billetter – betal ved inngangen» uansett åpent for publikum, og Vipps-betaling stengt til oppsettet er ferdig. Bryteren bestemmer om valget også skal finnes etter at Vipps er klart.")}
                      </p>
                    </div>
                    <Switch
                      id="ev-pay-at-door"
                      checked={!!form.pay_at_door}
                      onCheckedChange={(v) => set("pay_at_door", v)}
                      data-testid="editor-pay-at-door-switch"
                    />
                  </div>
                )}
                </>
              )}
              <div className="space-y-1.5">
                <Label>{t("Arrangementsbilde (valgfritt)")}</Label>
                {form.image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-border">
                    <img src={imageSrc(form.image_url)} alt={t("Forhåndsvisning")} className="w-full aspect-[16/9] object-cover" data-testid="editor-image-preview" />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={openCropExisting}
                        data-testid="editor-image-crop-button"
                      >
                        <Crop className="w-4 h-4 mr-1" /> {t("Beskjær")}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => set("image_url", "")}
                        data-testid="editor-image-remove-button"
                      >
                        <X className="w-4 h-4 mr-1" /> {t("Fjern")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full rounded-xl border-2 border-dashed border-border bg-secondary/50 hover:bg-secondary transition-colors duration-150 p-8 flex flex-col items-center gap-2 text-muted-foreground"
                    data-testid="editor-image-upload-button"
                  >
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImageIcon className="w-6 h-6" />}
                    <span className="text-sm font-medium">{uploading ? t("Laster opp...") : t("Last opp bilde")}</span>
                    <span className="text-xs">{t("JPG, PNG eller WEBP – maks 5 MB (større bilder komprimeres automatisk)")}</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="editor-image-file-input"
                />
                {!form.image_url && (
                  <div className="flex items-center gap-2 pt-1">
                    <Upload className="w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      type="url"
                      value={form.image_url}
                      onChange={(e) => set("image_url", e.target.value)}
                      placeholder={t("...eller lim inn en bilde-URL (https://...)")}
                      className="h-9 text-sm"
                      data-testid="editor-image-input"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ev-desc">{t("Beskrivelse")}</Label>
                <Textarea
                  id="ev-desc"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder={t("Fortell publikum hva de kan forvente...")}
                  rows={5}
                  data-testid="editor-description-input"
                />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border p-4">
                <div>
                  <p className="font-medium text-sm">{t("Publiser arrangementet")}</p>
                  <p className="text-xs text-muted-foreground">
                    {organizer?.status === "pending"
                      ? t("Kontoen venter på godkjenning – publisering blir tilgjengelig når kontoen er godkjent.")
                      : t("Publiserte arrangementer er synlige for alle.")}
                  </p>
                </div>
                <Switch
                  checked={form.published}
                  onCheckedChange={(v) => set("published", v)}
                  disabled={organizer?.status === "pending" || organizer?.status === "rejected"}
                  data-testid="editor-published-switch"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1 h-11" disabled={submitting || uploading} data-testid="editor-submit-button">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Lagrer...")}</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />{isEdit ? t("Lagre endringer") : t("Opprett arrangement")}</>
                  )}
                </Button>
                <Button type="button" variant="secondary" className="h-11" onClick={() => navigate("/dashboard")} data-testid="editor-cancel-button">
                  {t("Avbryt")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        {isEdit && !isExternal && (
          <Card className="border-border mt-6" data-testid="editor-discount-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <BadgePercent className="w-5 h-5" /> {t("Rabattkoder")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("Gi publikum prisavslag med en kode ved bestilling. Rabatten trekkes fra totalsummen (gjelder kun billetter med pris).")}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-[1fr_130px_90px_90px_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label htmlFor="dc-code" className="text-xs">{t("Kode")}</Label>
                  <Input
                    id="dc-code"
                    value={dcForm.code}
                    onChange={(e) => setDcForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s+/g, "") }))}
                    placeholder={t("F.eks. VELKOMMEN20")}
                    className="font-mono uppercase"
                    maxLength={40}
                    data-testid="discount-code-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("Type")}</Label>
                  <Select value={dcForm.type} onValueChange={(v) => setDcForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">{t("Prosent (%)")}</SelectItem>
                      <SelectItem value="fixed">{t("Fast beløp (kr)")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="dc-value" className="text-xs">{dcForm.type === "percent" ? t("Prosent") : t("Beløp (kr)")}</Label>
                  <Input
                    id="dc-value"
                    type="number"
                    min={1}
                    max={dcForm.type === "percent" ? 100 : 100000}
                    value={dcForm.value}
                    onChange={(e) => setDcForm((f) => ({ ...f, value: e.target.value }))}
                    data-testid="discount-value-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="dc-max" className="text-xs">{t("Maks bruk")}</Label>
                  <Input
                    id="dc-max"
                    type="number"
                    min={0}
                    value={dcForm.max_uses}
                    onChange={(e) => setDcForm((f) => ({ ...f, max_uses: e.target.value }))}
                    data-testid="discount-maxuses-input"
                  />
                </div>
                <Button type="button" onClick={addDiscountCode} disabled={dcSubmitting} className="col-span-2 sm:col-span-1" data-testid="discount-add-button">
                  {dcSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />{t("Legg til")}</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">{t("Maks bruk 0 = ubegrenset antall bestillinger kan bruke koden.")}</p>
              {discountCodes.length > 0 ? (
                <div className="space-y-2" data-testid="discount-code-list">
                  {discountCodes.map((dc) => (
                    <div key={dc.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5" data-testid={`discount-row-${dc.code}`}>
                      <span className="font-mono text-sm font-semibold">{dc.code}</span>
                      <Badge variant="secondary">{dc.type === "percent" ? `${dc.value}%` : `${dc.value} kr`} {t("rabatt")}</Badge>
                      <span className="flex-1 text-xs text-muted-foreground text-right">
                        {dc.max_uses > 0 ? t("Brukt {n} av {max}", { n: dc.uses, max: dc.max_uses }) : t("Brukt {n} ganger", { n: dc.uses })}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteDiscountCode(dc)}
                        className="rounded-full hover:bg-secondary p-1.5 text-muted-foreground hover:text-destructive"
                        data-testid={`discount-delete-${dc.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="discount-empty-state">{t("Ingen rabattkoder enda.")}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Beskjæringsdialog: velg 16:9-utsnitt før opplasting */}
      <Dialog open={cropOpen} onOpenChange={(o) => { if (!o) closeCrop(); }}>
        <DialogContent className="sm:max-w-xl" data-testid="crop-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("Beskjær bildet")}</DialogTitle>
            <DialogDescription>
              {t("Dra bildet og zoom for å velge utsnittet (16:9) – slik vises det på kort og arrangementssiden.")}
            </DialogDescription>
          </DialogHeader>
          <div className="relative w-full aspect-[16/10] rounded-lg overflow-hidden bg-muted" data-testid="crop-area">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={cropPos}
                zoom={cropZoom}
                aspect={16 / 9}
                onCropChange={setCropPos}
                onZoomChange={setCropZoom}
                onCropComplete={(_area, areaPixels) => setCropArea(areaPixels)}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
            <Slider
              value={[cropZoom]}
              min={1}
              max={3}
              step={0.01}
              onValueChange={(v) => setCropZoom(v[0])}
              className="flex-1"
              data-testid="crop-zoom-slider"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
          <DialogFooter className="gap-2">
            {pendingFileRef.current && (
              <Button type="button" variant="ghost" onClick={uploadOriginal} data-testid="crop-skip-button">
                {t("Bruk uten beskjæring")}
              </Button>
            )}
            <Button type="button" onClick={applyCrop} disabled={!cropArea} data-testid="crop-apply-button">
              <Crop className="w-4 h-4 mr-1.5" /> {t("Bruk utsnitt")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
